/**
 * Experiments chart wire → legacy adapter.
 *
 * `ui_experiments` emits `performance_trend` / `variant_comparison` in the
 * canonical wire shape (`{ data: [{x, values: [{series, value}]}], series:
 * [{name, label, color, y_axis_id}] | null }`). `ExperimentDetailPanel`
 * consumes the legacy snake_case shape (`{ data: [{x_axis_key_value,
 * ...series_keys}], x_axis_key, lines[], data_keys[], config }`).
 *
 * Lives in its own module (rather than inside `experiment-detail-slot.tsx`)
 * so the pure logic can be unit-tested without mounting the slot UI.
 *
 * Demo / report payloads already in the legacy shape flow through untouched.
 */

import type { ExperimentChartConfig } from '@/types/ui-blocks';
import { flattenAxisData, X_AXIS_KEY } from './chart-wire-adapters';
import type { ChartDataPoint } from './chart-wire-types';

interface ExperimentSeriesWire {
  name: string;
  label: string | null;
  color: string | null;
  y_axis_id: 'left' | 'right' | null;
}

interface ExperimentChartWire {
  data: ChartDataPoint[];
  series: ExperimentSeriesWire[] | null;
}

function isWireExperimentChartConfig(c: unknown): c is ExperimentChartWire {
  if (!c || typeof c !== 'object') return false;
  const obj = c as Record<string, unknown>;
  if (!Array.isArray(obj.data)) return false;
  if (obj.data.length === 0) {
    return 'series' in obj;
  }
  const first = obj.data[0];
  return typeof first === 'object' && first !== null && 'values' in (first as Record<string, unknown>);
}

/**
 * Auto-derive series declarations from data when the LLM omits them. The
 * schema for `experimentChartConfigSchema.series` is `.nullable()` with the
 * description "Null lets the renderer auto-derive from data points" — honour
 * that here so a null series doesn't render an empty chart. `y_axis_id`
 * stays null in derived series (no per-axis routing inferable from data
 * alone). Series names are emitted in first-seen order.
 */
function deriveExperimentSeriesFromData(data: ChartDataPoint[]): ExperimentSeriesWire[] {
  const names = new Set<string>();
  for (const point of data) {
    for (const v of point.values) {
      names.add(v.series);
    }
  }
  return Array.from(names).map((name) => ({
    name,
    label: null,
    color: null,
    y_axis_id: null
  }));
}

/**
 * Convert a wire-shape `ExperimentChartConfig` to the legacy shape consumed
 * by `ExperimentDetailPanel`. Returns the input unchanged if it's already
 * in legacy shape, and `undefined` if no chart was supplied.
 *
 * @param raw - The slot's `performance_trend` or `variant_comparison` value.
 * @returns Legacy `ExperimentChartConfig`, or `undefined` when absent.
 */
export function adaptExperimentChartConfig(raw: unknown): ExperimentChartConfig | undefined {
  if (raw == null) return undefined;
  if (!isWireExperimentChartConfig(raw)) {
    return raw as ExperimentChartConfig;
  }
  const data = flattenAxisData(raw.data) as Record<string, string | number>[];
  const series = raw.series ?? deriveExperimentSeriesFromData(raw.data);
  const lines = series.map((s) => ({
    data_key: s.name,
    color: s.color ?? '',
    ...(s.y_axis_id ? { y_axis_id: s.y_axis_id } : {})
  }));
  const dataKeys = series.map((s) => ({
    key: s.name,
    color: s.color ?? '',
    name: s.label ?? s.name
  }));
  const config = series.reduce<Record<string, { label: string; color: string }>>((acc, s) => {
    acc[s.name] = { label: s.label ?? s.name, color: s.color ?? '' };
    return acc;
  }, {});
  return { data, x_axis_key: X_AXIS_KEY, lines, data_keys: dataKeys, config };
}
